import React, { useState, useEffect, useCallback } from 'react';
import './CategoryPickerModal.css';
import { searchAthenaFlows, fetchAthenaFlowGroups } from '../services/api';

/**
 * FlowPickerModal — a large, searchable modal for choosing an Athena Flow.
 *
 * The flow library is ~8k flat rows, so we NEVER load it all at once. Instead:
 *   - open  → show the 16 OC23 top-level product groups (fast COUNT query),
 *   - click a group → server-fetch just that group's flows (LIMIT 300),
 *   - type in the search box → server-search across the whole library (LIMIT 300).
 * Visually mirrors CategoryPickerModal (reuses its CSS).
 *
 * Props: value, selectedLabel, onChange(flowObj|null), placeholder, title, disabled
 *   flowObj = { value, label, flow, flowTypeOc23, unit, flowGroup }
 */
const OC23_GROUP_TITLES = {
  '11': 'Site Products',
  '13': 'Structural and Exterior Enclosure Products',
  '15': 'Interior and Finish Products',
  '17': 'Openings, Passages, and Protection Products',
  '19': 'Specialty Products',
  '21': 'Furnishings, Fixtures and Equipment Products',
  '23': 'Conveying Systems and Material Handling Products',
  '25': 'Medical and Laboratory Equipment',
  '27': 'General Facility Services Products',
  '29': 'Facility and Occupant Protection Products',
  '31': 'Plumbing Specific Products and Equipment',
  '33': 'HVAC Specific Products and Equipment',
  '35': 'Electrical and Lighting Specific Products and Equipment',
  '37': 'Information and Communication Specific Products and Equipment',
  '39': 'Utility and Transportation Products',
  '99': 'Other Products',
};

const FlowPickerModal = ({
  value,
  selectedLabel,
  onChange,
  placeholder = 'Select an Athena flow…',
  title = 'Select Athena Flow',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [groups, setGroups] = useState([]);
  const [currentGroup, setCurrentGroup] = useState(null); // { code, count } | null
  const [results, setResults] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  const term = search.trim();
  const showGroups = !term && !currentGroup;

  const close = useCallback(() => {
    setOpen(false);
    setSearch('');
    setCurrentGroup(null);
    setResults([]);
  }, []);

  // Load the 16 top-level groups once, the first time the modal is opened.
  // Deps are [open] ONLY — do not add loadingGroups/groups.length, or setLoadingGroups(true)
  // re-triggers this effect, its cleanup flips `active` to false, and the arriving result is
  // discarded (loads forever). The groups.length guard prevents a refetch on later opens.
  useEffect(() => {
    if (!open || groups.length) return undefined;
    let active = true;
    setLoadingGroups(true);
    (async () => {
      try {
        const rows = await fetchAthenaFlowGroups();
        if (active) setGroups(rows || []);
      } catch (e) {
        console.error('❌ Error loading flow groups:', e);
        if (active) setGroups([]);
      } finally {
        if (active) setLoadingGroups(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fetch the active list: search across all (when typing) or one group's flows.
  useEffect(() => {
    if (!open || showGroups) { setResults([]); return undefined; }
    let active = true;
    setLoadingList(true);
    const t = setTimeout(async () => {
      try {
        const rows = term
          ? await searchAthenaFlows(term, '')
          : await searchAthenaFlows('', currentGroup.code);
        if (active) setResults(rows || []);
      } catch (e) {
        if (active) setResults([]);
      } finally {
        if (active) setLoadingList(false);
      }
    }, term ? 250 : 0);
    return () => { active = false; clearTimeout(t); };
  }, [open, term, currentGroup, showGroups]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  const pick = (row) => {
    onChange({
      value: row.Id,
      label: `${row.Flow} — ${row.Flow_Type_OC23}`,
      flow: row.Flow,
      flowTypeOc23: row.Flow_Type_OC23,
      unit: row.Unit,
      flowGroup: row.Flow_Group,
    });
    close();
  };

  const capped = results.length >= 300;

  return (
    <>
      <div
        className={`cpm-trigger${value ? '' : ' cpm-trigger--empty'}`}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setOpen(true)}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span className="cpm-trigger__label">{selectedLabel || placeholder}</span>
        <span className="cpm-trigger__icon">🔍</span>
      </div>

      {open && (
        <div className="cpm-overlay" onClick={close}>
          <div className="cpm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cpm-header">
              <h3 className="cpm-title">{title}</h3>
              <button type="button" className="cpm-close" onClick={close} aria-label="Close">×</button>
            </div>

            <div className="cpm-search">
              <input
                autoFocus
                type="text"
                value={search}
                placeholder="Search all Athena flows…"
                onChange={(e) => setSearch(e.target.value)}
              />
              <div className="cpm-search__count">
                {loadingGroups || loadingList
                  ? 'Loading…'
                  : showGroups
                  ? `${groups.length} product groups`
                  : capped
                  ? `${results.length}+ — refine your search`
                  : `${results.length} ${results.length === 1 ? 'flow' : 'flows'}`}
              </div>
            </div>

            {/* Breadcrumb / back link when drilled into a group (and not searching) */}
            {!term && currentGroup && (
              <div
                style={{
                  flex: 'none', display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 22px', background: '#f7faf9', borderBottom: '1px solid #f0f4f3',
                }}
              >
                <button
                  type="button"
                  onClick={() => setCurrentGroup(null)}
                  style={{ border: 'none', background: 'none', color: '#2f7d72', fontWeight: 700, fontSize: 13, cursor: 'pointer', padding: 0 }}
                >
                  ← All product groups
                </button>
                <span style={{ color: '#cbd5d1' }}>/</span>
                <span style={{ fontWeight: 700, color: '#173a34', fontSize: 14 }}>
                  [{currentGroup.code}] {OC23_GROUP_TITLES[currentGroup.code] || 'Products'}
                </span>
              </div>
            )}

            <div className="cpm-body">
              {loadingGroups && showGroups ? (
                <div className="cpm-empty">Loading groups…</div>
              ) : showGroups ? (
                groups.length === 0 ? (
                  <div className="cpm-empty">No flow groups available.</div>
                ) : (
                  groups.map((g) => (
                    <button
                      type="button"
                      key={g.code}
                      className="cpm-row cpm-row--leaf"
                      onClick={() => setCurrentGroup(g)}
                    >
                      <span className="cpm-row__name" style={{ fontWeight: 700, color: '#173a34' }}>
                        <span style={{ color: '#6b7280' }}>[{g.code}]</span>{' '}
                        {OC23_GROUP_TITLES[g.code] || 'Products'}
                      </span>
                      <span className="cpm-count">{g.count}</span>
                      <span className="cpm-chevron" style={{ color: '#9ca3af', transform: 'none' }}>▶</span>
                    </button>
                  ))
                )
              ) : loadingList ? (
                <div className="cpm-empty">Loading flows…</div>
              ) : results.length === 0 ? (
                <div className="cpm-empty">
                  {term ? `No flows match “${search}”.` : 'No flows in this group.'}
                </div>
              ) : (
                results.map((row) => (
                  <button
                    type="button"
                    key={row.Id}
                    className={`cpm-row cpm-row--leaf${value === row.Id ? ' cpm-row--selected' : ''}`}
                    onClick={() => pick(row)}
                  >
                    {value === row.Id && <span className="cpm-check">✓</span>}
                    <span className="cpm-row__name">
                      {row.Flow}{' '}
                      <span style={{ color: '#7f8c8d' }}>
                        — {row.Flow_Type_OC23}
                        {row.Unit ? ` · ${row.Unit}` : ''}
                      </span>
                    </span>
                  </button>
                ))
              )}
            </div>

            <div className="cpm-footer">
              {value ? (
                <button type="button" className="cpm-clear" onClick={() => { onChange(null); close(); }}>
                  ✕ Clear selection
                </button>
              ) : (
                <span />
              )}
              <button type="button" className="cpm-done" onClick={close}>Done</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FlowPickerModal;
